/**
 * AdminMediaLibrary — mobile-first investigation photo pipeline (/admin/media).
 *
 * Upload from a phone gallery (multi-select, HEIC) → durable server-side
 * DRAFTs → metadata, attachments, review, publish. The ASSET HEALTH panel
 * is the owner's maintenance dashboard: database-backed one-tap filters,
 * per-card health badges, bulk actions and a CSV metadata export.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ImagePlus, Loader2, RefreshCw, X, Check, Link2, Trash2, Eye, Upload,
  CheckCircle2, AlertTriangle, Send, Archive, RotateCcw, Ban, Activity,
  CheckSquare, Square, Download, BadgeCheck, ChevronDown, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import AdminWikiReviewQueue from "./AdminWikiReviewQueue";
import {
  mediaList, mediaUpload, mediaPatch, mediaAttach, mediaDetach,
  mediaAction, mediaDelete, mediaBlobUrl,
  mediaHealth, mediaBulk, mediaExportCsv,
} from "../../lib/api";
import { AuthImg } from "../media/AuthImg";
import { MEDIA_ROLES, MEDIA_TARGET_TYPES } from "../media/mediaRoles";
import { StepUpGateModal, isStepUpRequired } from "./StepUpGateModal";

const FILTERS = ["all", "draft", "review", "published", "archived"];
const STATUS_COLOR = {
  draft: "#8A8578", review: "#E0A526", published: "#3FB950",
  rejected: "#D92D20", archived: "#5A554C",
};
const HEALTH_CHIPS = [
  ["drafts", "Drafts"],
  ["review_ready", "Review ready"],
  ["published", "Published"],
  ["published_unattached", "Published · unattached"],
  ["missing_caption", "No caption"],
  ["missing_source", "No source"],
  ["missing_credit", "No credit"],
  ["missing_rights", "No rights"],
  ["missing_verification", "Unverified"],
  ["no_derivatives", "No derivatives"],
  ["duplicate_candidates", "Duplicates"],
  ["multi_use", "Multi-use"],
  ["restricted", "Restricted"],
  ["archived", "Archived"],
  ["recently_uploaded", "New this week"],
  ["recently_published", "Published this week"],
];
const FLAG_BADGES = [
  ["missing_caption", "CAP", "Missing caption"],
  ["missing_source", "SRC", "Missing source"],
  ["missing_credit", "CRD", "Missing credit"],
  ["missing_rights", "RTS", "Missing rights"],
  ["missing_verification", "VER", "Unverified"],
  ["duplicate", "DUP", "Duplicate candidate"],
  ["no_derivatives", "DRV", "No derivatives"],
];
const uid = () => Math.random().toString(36).slice(2);

const StatusChip = ({ status }) => (
  <span className="font-mono text-[8px] uppercase tracking-[0.2em] px-1.5 py-0.5"
        style={{ background: STATUS_COLOR[status] || "#333", color: "#0a0907" }}
        data-testid={`media-status-${status}`}>{status}</span>
);

const FlagBadges = ({ flags }) => {
  if (!flags) return null;
  const active = FLAG_BADGES.filter(([k]) => flags[k]);
  if (!active.length) return null;
  return (
    <div className="absolute bottom-1 left-1 flex flex-wrap gap-0.5 max-w-[80%]" data-testid="media-flag-badges">
      {active.slice(0, 4).map(([k, code, title]) => (
        <span key={k} title={title}
              className="font-mono text-[7px] px-1 py-0.5 bg-black/75 border border-[#E0A526]/60 text-[#E0A526]">
          {code}
        </span>
      ))}
      {active.length > 4 && (
        <span className="font-mono text-[7px] px-1 py-0.5 bg-black/75 text-[#E0A526]">+{active.length - 4}</span>
      )}
    </div>
  );
};

/* ---------- Detail sheet ---------- */
const DetailSheet = ({ token, userToken, asset, onClose, onChange, onDeleted, onStepUp }) => {
  const [a, setA] = useState(asset);
  const [meta, setMeta] = useState({
    caption: asset.caption || "", source: asset.source || "",
    rights: asset.rights || "", credit: asset.credit || "",
    verification: asset.verification || "unverified", notes: asset.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [att, setAtt] = useState({ target_type: "dossier", target_id: "", role: "gallery" });
  const [busy, setBusy] = useState("");

  const refresh = (updated) => { setA(updated); onChange(updated); };

  const saveMeta = async () => {
    setSaving(true);
    try { refresh(await mediaPatch(token, a.id, meta, userToken)); toast.success("Metadata saved"); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const doAttach = async () => {
    if (!att.target_id.trim()) return toast.error("Enter a record ID/slug");
    try { refresh(await mediaAttach(token, a.id, att, userToken)); setAtt({ ...att, target_id: "" }); toast.success("Attached"); }
    catch { toast.error("Attach failed"); }
  };
  const doDetach = async (x) => {
    try { refresh(await mediaDetach(token, a.id, { target_type: x.target_type, target_id: x.target_id }, userToken)); }
    catch { toast.error("Detach failed"); }
  };
  const doAction = async (action) => {
    setBusy(action);
    try { refresh(await mediaAction(token, a.id, action, userToken)); toast.success(`Photo ${action}`); }
    catch (e) {
      if (isStepUpRequired(e)) onStepUp(() => doAction(action));
      else toast.error(e?.response?.data?.detail || `${action} failed`);
    }
    finally { setBusy(""); }
  };
  const execDelete = async () => {
    try { await mediaDelete(token, a.id, userToken); toast.success("Deleted"); onDeleted(a.id); }
    catch (e) {
      if (isStepUpRequired(e)) onStepUp(execDelete);
      else toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };
  const doDelete = () => {
    if (!window.confirm("Delete this photo permanently?")) return;
    execDelete();
  };

  const ActBtn = ({ action, label, Icon, tone }) => (
    <button onClick={() => doAction(action)} disabled={!!busy}
            data-testid={`media-action-${action}`}
            className="flex-1 min-w-[46%] inline-flex items-center justify-center gap-2 min-h-[48px] border font-mono text-[10px] uppercase tracking-[0.18em] transition-colors disabled:opacity-40"
            style={{ borderColor: tone || "#454038", color: tone || "#C9C2B4" }}>
      {busy === action ? <Loader2 size={13} className="animate-spin" /> : <Icon size={13} />} {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-[200] bg-black/80 flex sm:items-center sm:justify-center overflow-y-auto"
         data-testid="media-detail-sheet" onClick={onClose}>
      <div className="bg-[#12110e] border border-[#2D2A26] w-full sm:max-w-lg sm:my-8 min-h-full sm:min-h-0"
           onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#12110e] border-b border-[#2D2A26] flex items-center gap-2 px-4 py-3 z-10">
          <StatusChip status={a.status} />
          <span className="font-mono text-[10px] text-[#7B7168] truncate">{a.original_filename}</span>
          <button onClick={onClose} data-testid="media-detail-close"
                  className="ml-auto w-10 h-10 inline-flex items-center justify-center text-[#A39E93]"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-4">
          <AuthImg token={token} userToken={userToken} assetId={a.id} kind="webp"
                   className="w-full max-h-[42vh] object-contain bg-black border border-[#2D2A26]" />
          <div className="flex flex-wrap gap-2 font-mono text-[9px] text-[#7B7168]">
            <span>{a.width}×{a.height}</span>
            <button type="button"
                    onClick={async () => {
                      try { window.open(await mediaBlobUrl(token, a.id, null, userToken), "_blank"); }
                      catch { toast.error("Could not load original"); }
                    }}
                    data-testid="media-view-original"
                    className="inline-flex items-center gap-1 underline text-[#9FA8D4]"><Eye size={11} /> View original (private)</button>
          </div>

          {/* Metadata */}
          <div className="space-y-2">
            {[["caption", "Caption"], ["source", "Source"], ["rights", "Rights / licence"], ["credit", "Credit"]].map(([k, ph]) => (
              <input key={k} value={meta[k]} placeholder={ph}
                     onChange={(e) => setMeta({ ...meta, [k]: e.target.value })}
                     data-testid={`media-meta-${k}`}
                     className="w-full bg-[#0F0E0B] border border-[#2D2A26] px-3 min-h-[48px] text-[16px] text-[#E4DFD3] placeholder:text-[#4A463F] focus:outline-none focus:border-[#A68F5E]" />
            ))}
            <select value={meta.verification} onChange={(e) => setMeta({ ...meta, verification: e.target.value })}
                    data-testid="media-meta-verification"
                    className="w-full bg-[#0F0E0B] border border-[#2D2A26] px-3 min-h-[48px] text-[16px] text-[#E4DFD3] focus:outline-none focus:border-[#A68F5E]">
              <option value="unverified">Unverified</option>
              <option value="verified">Verified</option>
              <option value="disputed">Disputed</option>
            </select>
            <textarea value={meta.notes} placeholder="Internal notes" rows={2}
                      onChange={(e) => setMeta({ ...meta, notes: e.target.value })}
                      data-testid="media-meta-notes"
                      className="w-full bg-[#0F0E0B] border border-[#2D2A26] px-3 py-2 text-[16px] text-[#E4DFD3] placeholder:text-[#4A463F] focus:outline-none focus:border-[#A68F5E]" />
            <button onClick={saveMeta} disabled={saving} data-testid="media-save-meta"
                    className="w-full inline-flex items-center justify-center gap-2 min-h-[50px] bg-[#A68F5E] text-[#0a0907] font-mono text-[11px] uppercase tracking-[0.2em] disabled:opacity-50">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save metadata
            </button>
          </div>

          {/* Attachments */}
          <div className="border-t border-[#2D2A26] pt-3">
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#7B7168] mb-2">Attach to record</div>
            <div className="flex gap-2 mb-2">
              <select value={att.target_type} onChange={(e) => setAtt({ ...att, target_type: e.target.value })}
                      data-testid="media-attach-type"
                      className="bg-[#0F0E0B] border border-[#2D2A26] px-2 min-h-[48px] text-[14px] text-[#E4DFD3] focus:outline-none focus:border-[#A68F5E]">
                {MEDIA_TARGET_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input value={att.target_id} placeholder="record ID / slug"
                     onChange={(e) => setAtt({ ...att, target_id: e.target.value })}
                     data-testid="media-attach-id"
                     className="flex-1 min-w-0 bg-[#0F0E0B] border border-[#2D2A26] px-3 min-h-[48px] text-[16px] text-[#E4DFD3] placeholder:text-[#4A463F] focus:outline-none focus:border-[#A68F5E]" />
            </div>
            <select value={att.role} onChange={(e) => setAtt({ ...att, role: e.target.value })}
                    data-testid="media-attach-role-select"
                    className="w-full mb-2 bg-[#0F0E0B] border border-[#2D2A26] px-2 min-h-[48px] text-[14px] text-[#E4DFD3] focus:outline-none focus:border-[#A68F5E]">
              {MEDIA_ROLES.map((r) => <option key={r} value={r}>role: {r.replace(/_/g, " ")}</option>)}
            </select>
            <button onClick={doAttach} data-testid="media-attach-btn"
                    className="w-full inline-flex items-center justify-center gap-2 min-h-[46px] border border-[#454038] text-[#C9C2B4] font-mono text-[10px] uppercase tracking-[0.18em]">
              <Link2 size={13} /> Attach
            </button>
            <div className="mt-2 space-y-1">
              {(a.attachments || []).map((x, i) => (
                <div key={i} className="flex items-center gap-2 bg-[#0F0E0B] border border-[#2D2A26] px-2 py-2">
                  <span className="font-mono text-[8px] uppercase tracking-[0.15em] text-[#9FA8D4]">{x.target_type}</span>
                  <span className="font-mono text-[11px] text-[#E4DFD3] truncate">{x.target_id}</span>
                  {x.role && <span className="font-mono text-[8px] uppercase text-[#A68F5E] shrink-0">{x.role.replace(/_/g, " ")}</span>}
                  <button onClick={() => doDetach(x)} className="ml-auto text-[#7B7168] w-9 h-9 inline-flex items-center justify-center"><X size={14} /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Lifecycle */}
          <div className="border-t border-[#2D2A26] pt-3">
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#7B7168] mb-2">Review & publish</div>
            <div className="flex flex-wrap gap-2">
              {(a.status === "draft" || a.status === "rejected") && <ActBtn action="submit" label="Submit for review" Icon={Send} />}
              {a.status !== "published" && a.status !== "archived" && <ActBtn action="publish" label="Publish" Icon={CheckCircle2} tone="#3FB950" />}
              {a.status === "published" && <ActBtn action="unpublish" label="Unpublish" Icon={Ban} tone="#E0A526" />}
              {a.status !== "rejected" && a.status !== "archived" && <ActBtn action="reject" label="Reject" Icon={AlertTriangle} tone="#D92D20" />}
              {a.status !== "archived" && <ActBtn action="archive" label="Archive" Icon={Archive} />}
              {a.status === "archived" && <ActBtn action="restore" label="Restore" Icon={RotateCcw} />}
            </div>
            <button onClick={doDelete} data-testid="media-delete"
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 min-h-[44px] border border-[#D92D20] text-[#D92D20] font-mono text-[10px] uppercase tracking-[0.18em]">
              <Trash2 size={13} /> Delete permanently (Owner)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------- Bulk attach inline form ---------- */
const BulkAttachForm = ({ onSubmit, onCancel }) => {
  const [tt, setTt] = useState("dossier");
  const [tid, setTid] = useState("");
  const [role, setRole] = useState("gallery");
  return (
    <div className="flex flex-wrap gap-2 items-center pt-2" data-testid="bulk-attach-form">
      <select value={tt} onChange={(e) => setTt(e.target.value)}
              className="bg-[#0F0E0B] border border-[#2D2A26] px-2 min-h-[44px] text-[13px] text-[#E4DFD3]">
        {MEDIA_TARGET_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      <input value={tid} onChange={(e) => setTid(e.target.value)} placeholder="record ID / slug"
             data-testid="bulk-attach-id"
             className="flex-1 min-w-[120px] bg-[#0F0E0B] border border-[#2D2A26] px-3 min-h-[44px] text-[16px] text-[#E4DFD3] placeholder:text-[#4A463F]" />
      <select value={role} onChange={(e) => setRole(e.target.value)}
              className="bg-[#0F0E0B] border border-[#2D2A26] px-2 min-h-[44px] text-[13px] text-[#E4DFD3]">
        {MEDIA_ROLES.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
      </select>
      <button onClick={() => tid.trim() ? onSubmit({ target_type: tt, target_id: tid.trim(), role }) : toast.error("Enter a record ID")}
              data-testid="bulk-attach-go"
              className="min-h-[44px] px-3 border border-[#A68F5E] text-[#A68F5E] font-mono text-[10px] uppercase tracking-[0.15em]">
        <Link2 size={12} className="inline mr-1" /> Attach
      </button>
      <button onClick={onCancel} className="min-h-[44px] px-2 text-[#7B7168]"><X size={14} /></button>
    </div>
  );
};

export const AdminMediaLibrary = ({ token, userToken }) => {
  const [assets, setAssets] = useState([]);
  const [counts, setCounts] = useState({});
  const [health, setHealth] = useState(null);           // counts from /health
  const [healthOpen, setHealthOpen] = useState(true);
  const [healthKey, setHealthKey] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("health") || ""; }
    catch { return ""; }
  });       // active health filter
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [queue, setQueue] = useState([]);
  const [detail, setDetail] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState("");
  const [attachOpen, setAttachOpen] = useState(false);
  const [stepupRetry, setStepupRetry] = useState(null);   // fn to resume after step-up
  const fileRef = useRef(null);
  const batchRef = useRef(uid());

  const loadHealth = useCallback(async () => {
    try { setHealth(await mediaHealth(token, userToken)); } catch { /* panel shows dashes */ }
  }, [token, userToken]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: 80,
        ...(healthKey ? { health: healthKey } : {}),
        ...(filter !== "all" && !healthKey ? { status: filter } : {}),
        ...(q ? { q } : {}),
      };
      const d = await mediaList(token, params, userToken);
      setAssets(d.assets || []); setCounts(d.status_counts || {});
    } catch { toast.error("Failed to load media"); }
    finally { setLoading(false); }
  }, [token, userToken, filter, q, healthKey]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadHealth(); }, [loadHealth]);

  const uploadOne = useCallback(async (item) => {
    setQueue((Q) => Q.map((x) => x.key === item.key ? { ...x, status: "uploading", progress: 0, error: "" } : x));
    try {
      const asset = await mediaUpload(token, item.file, batchRef.current,
        (p) => setQueue((Q) => Q.map((x) => x.key === item.key ? { ...x, progress: p } : x)), userToken);
      setQueue((Q) => Q.map((x) => x.key === item.key ? { ...x, status: "done", progress: 100 } : x));
      setAssets((A) => [asset, ...A]);
      setCounts((c) => ({ ...c, draft: (c.draft || 0) + 1 }));
      loadHealth();
    } catch (e) {
      setQueue((Q) => Q.map((x) => x.key === item.key ? { ...x, status: "error", error: e?.response?.data?.detail || "Upload failed" } : x));
    }
  }, [token, userToken, loadHealth]);

  const addFiles = useCallback((files) => {
    const items = Array.from(files).map((file) => ({
      key: uid(), file, name: file.name, status: "queued", progress: 0,
      preview: URL.createObjectURL(file),
    }));
    if (!items.length) return;
    setQueue((Q) => [...items, ...Q]);
    items.forEach((it) => uploadOne(it));
  }, [uploadOne]);

  const onPick = (e) => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = ""; };
  const doneCount = queue.filter((x) => x.status === "done").length;

  /* ----- selection & bulk ----- */
  const toggleSel = (id) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const exitSelect = () => { setSelectMode(false); setSelected(new Set()); setAttachOpen(false); };

  const runBulk = async (action, extra = {}) => {
    if (!selected.size) return toast.error("Nothing selected");
    setBulkBusy(action);
    try {
      const r = await mediaBulk(token, { ids: [...selected], action, ...extra }, userToken);
      toast.success(`${r.ok} asset${r.ok === 1 ? "" : "s"} — ${action}${r.failed ? ` · ${r.failed} failed` : ""}`);
      exitSelect(); load(); loadHealth();
    } catch (e) {
      if (isStepUpRequired(e)) setStepupRetry(() => () => runBulk(action, extra));
      else toast.error(e?.response?.data?.detail || `Bulk ${action} failed`);
    } finally { setBulkBusy(""); }
  };

  const BulkBtn = ({ action, label, Icon, tone, onClick }) => (
    <button onClick={onClick || (() => runBulk(action))} disabled={!!bulkBusy || !selected.size}
            data-testid={`bulk-${action}`}
            className="inline-flex items-center gap-1.5 min-h-[44px] px-2.5 border font-mono text-[9px] uppercase tracking-[0.15em] disabled:opacity-40 transition-colors"
            style={{ borderColor: tone || "#454038", color: tone || "#C9C2B4" }}>
      {bulkBusy === action ? <Loader2 size={12} className="animate-spin" /> : <Icon size={12} />} {label}
    </button>
  );

  return (
    <div className="space-y-4 pb-28" data-testid="admin-media-library">
      <div className="flex flex-wrap items-center gap-2">
        <ImagePlus size={16} className="text-[#9FA8D4]" />
        <h2 className="font-display text-lg text-[#E4DFD3] tracking-wide">MEDIA LIBRARY</h2>
        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#7B7168]">
          {counts.published || 0} published · {counts.draft || 0} draft · {counts.review || 0} in review
        </span>
      </div>

      {/* Upload — the primary action, large and obvious */}
      <input ref={fileRef} type="file" accept="image/*,.heic,.heif,.HEIC,.HEIF" multiple
             onChange={onPick} className="hidden" data-testid="media-file-input" />
      <button
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files); }}
        data-testid="media-upload-btn"
        className="w-full inline-flex flex-col items-center justify-center gap-2 min-h-[110px] border-2 border-dashed transition-colors"
        style={{ borderColor: dragOver ? "#A68F5E" : "#454038", background: dragOver ? "#161318" : "#0F0E0B" }}>
        <Upload size={26} className="text-[#A68F5E]" />
        <span className="font-mono text-[13px] uppercase tracking-[0.2em] text-[#E4DFD3]">Upload Images</span>
        <span className="font-mono text-[9px] text-[#7B7168]">Tap to pick from your photo gallery · multiple photos · HEIC supported</span>
      </button>

      {/* Upload queue with per-photo progress + retry */}
      {queue.length > 0 && (
        <div className="border border-[#2D2A26]" data-testid="media-upload-queue">
          <div className="px-3 py-2 border-b border-[#2D2A26] flex items-center gap-2">
            <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#7B7168]">Uploading — {doneCount}/{queue.length} done</span>
            {doneCount === queue.length && (
              <button onClick={() => setQueue([])} className="ml-auto font-mono text-[9px] uppercase tracking-[0.2em] text-[#9FA8D4]" data-testid="media-queue-clear">Clear</button>
            )}
          </div>
          <div className="divide-y divide-[#2D2A26] max-h-64 overflow-y-auto">
            {queue.map((x) => (
              <div key={x.key} className="flex items-center gap-3 px-3 py-2" data-testid={`media-queue-item-${x.status}`}>
                <img src={x.preview} alt="" className="w-12 h-12 object-cover border border-[#2D2A26]" />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[11px] text-[#E4DFD3] truncate">{x.name}</div>
                  <div className="h-1.5 mt-1 bg-[#2D2A26]">
                    <div className="h-full transition-all"
                         style={{ width: `${x.progress}%`, background: x.status === "error" ? "#D92D20" : x.status === "done" ? "#3FB950" : "#A68F5E" }} />
                  </div>
                  {x.error && <div className="font-mono text-[9px] text-[#D92D20] mt-0.5">{x.error}</div>}
                </div>
                {x.status === "uploading" && <Loader2 size={15} className="animate-spin text-[#A68F5E]" />}
                {x.status === "done" && <CheckCircle2 size={15} className="text-[#3FB950]" />}
                {x.status === "error" && (
                  <button onClick={() => uploadOne(x)} data-testid="media-retry"
                          className="inline-flex items-center gap-1 min-h-[40px] px-2 border border-[#D92D20] text-[#D92D20] font-mono text-[9px] uppercase">
                    <RefreshCw size={12} /> Retry
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* WIKIMEDIA REVIEW QUEUE — held fuzzy-match ingests awaiting decision */}
      <AdminWikiReviewQueue token={token} userToken={userToken} />

      {/* ASSET HEALTH — database-backed one-tap maintenance filters */}
      <div className="border border-[#2D2A26]" data-testid="asset-health-panel">
        <div className="w-full flex items-center gap-2 px-3 min-h-[46px]">
          <button onClick={() => setHealthOpen((v) => !v)} data-testid="asset-health-toggle"
                  className="flex-1 flex items-center gap-2 text-left min-h-[46px]">
            <Activity size={13} className="text-[#E0A526]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E4DFD3]">Asset Health</span>
            {healthKey && (
              <span className="font-mono text-[8px] uppercase tracking-[0.15em] px-1.5 py-0.5 bg-[#E0A526] text-[#0a0907]">
                filtered
              </span>
            )}
          </button>
          <span className="ml-auto flex items-center gap-3">
            <button type="button"
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        const u = await mediaExportCsv(token, userToken);
                        const a = document.createElement("a");
                        a.href = u; a.download = "media-library.csv"; a.click();
                      } catch { toast.error("Export failed"); }
                    }}
                    data-testid="media-export-csv"
                    className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.15em] text-[#9FA8D4] underline underline-offset-2">
              <Download size={11} /> CSV
            </button>
            <button type="button" onClick={() => setHealthOpen((v) => !v)} aria-label="Toggle asset health" className="text-[#7B7168]">
              {healthOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          </span>
        </div>
        {healthOpen && (
          <div className="px-3 pb-3 flex flex-wrap gap-1.5" data-testid="asset-health-chips">
            {HEALTH_CHIPS.map(([key, label]) => {
              const n = health ? (health[key] ?? 0) : null;
              const active = healthKey === key;
              return (
                <button key={key}
                        onClick={() => { setHealthKey(active ? "" : key); setFilter("all"); }}
                        data-testid={`health-chip-${key}`}
                        className="inline-flex items-center gap-1.5 min-h-[40px] px-2.5 border font-mono text-[9px] uppercase tracking-[0.12em] transition-colors"
                        style={{
                          borderColor: active ? "#E0A526" : n ? "#454038" : "#2D2A26",
                          color: active ? "#E0A526" : n ? "#C9C2B4" : "#5A554C",
                          background: active ? "#1a1408" : "transparent",
                        }}>
                  {label}
                  <span className="font-mono text-[9px]" style={{ color: active ? "#E0A526" : n ? "#A68F5E" : "#454038" }}>
                    {n == null ? "—" : n}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Filters + search + select-mode toggle */}
      <div className="flex flex-wrap gap-2 items-center">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => { setFilter(f); setHealthKey(""); }} data-testid={`media-filter-${f}`}
                  className="px-3 min-h-[40px] border font-mono text-[9px] uppercase tracking-[0.18em] transition-colors"
                  style={{ borderColor: filter === f && !healthKey ? "#9FA8D4" : "#2D2A26", color: filter === f && !healthKey ? "#9FA8D4" : "#7B7168" }}>
            {f}{f !== "all" && counts[f] != null ? ` (${counts[f]})` : ""}
          </button>
        ))}
        <button onClick={() => (selectMode ? exitSelect() : setSelectMode(true))}
                data-testid="media-select-mode"
                className="px-3 min-h-[40px] border font-mono text-[9px] uppercase tracking-[0.18em] inline-flex items-center gap-1.5 transition-colors"
                style={{ borderColor: selectMode ? "#E0A526" : "#2D2A26", color: selectMode ? "#E0A526" : "#7B7168" }}>
          {selectMode ? <CheckSquare size={12} /> : <Square size={12} />} Select
        </button>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search caption / source…"
               data-testid="media-search"
               className="flex-1 min-w-[140px] bg-[#0F0E0B] border border-[#2D2A26] px-3 min-h-[40px] text-[16px] text-[#E4DFD3] placeholder:text-[#4A463F] focus:outline-none focus:border-[#A68F5E]" />
      </div>

      {/* Grid — large tap-friendly cards with health badges */}
      {loading ? (
        <div className="flex items-center gap-2 py-10 justify-center text-[#7B7168] font-mono text-xs">
          <Loader2 size={14} className="animate-spin" /> LOADING…
        </div>
      ) : assets.length === 0 ? (
        <div className="border border-[#2D2A26] px-4 py-10 text-center font-mono text-[11px] text-[#7B7168]" data-testid="media-empty">
          {healthKey
            ? "No assets match this health filter — this category is clean."
            : "No photos yet. Tap Upload Images to add investigation photographs from your phone."}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="media-grid">
          {assets.map((a) => {
            const isSel = selected.has(a.id);
            return (
              <button key={a.id}
                      onClick={() => (selectMode ? toggleSel(a.id) : setDetail(a))}
                      data-testid={`media-card-${a.id}`}
                      className="text-left border bg-[#0F0E0B] overflow-hidden transition-colors"
                      style={{ borderColor: isSel ? "#E0A526" : "#2D2A26" }}>
                <div className="relative aspect-square bg-black">
                  <AuthImg token={token} userToken={userToken} assetId={a.id}
                           className="w-full h-full object-cover" />
                  <div className="absolute top-1 left-1"><StatusChip status={a.status} /></div>
                  {selectMode && (
                    <div className="absolute top-1 right-1 w-7 h-7 flex items-center justify-center bg-black/70"
                         data-testid={`media-card-check-${a.id}`}>
                      {isSel ? <CheckSquare size={16} className="text-[#E0A526]" /> : <Square size={16} className="text-[#7B7168]" />}
                    </div>
                  )}
                  {!selectMode && (a.attachments || []).length > 0 && (
                    <div className="absolute top-1 right-1 bg-black/70 px-1.5 py-0.5 font-mono text-[8px] text-[#9FA8D4] inline-flex items-center gap-1">
                      <Link2 size={9} /> {a.attachments.length}
                    </div>
                  )}
                  <FlagBadges flags={a.flags} />
                </div>
                <div className="px-2 py-1.5">
                  <div className="font-mono text-[10px] text-[#E4DFD3] truncate">{a.caption || a.original_filename}</div>
                  <div className="font-mono text-[8px] text-[#7B7168] truncate">{a.source || "—"}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Bulk actions bar — fixed, thumb-reachable on mobile */}
      {selectMode && (
        <div className="fixed bottom-0 left-0 right-0 z-[150] bg-[#12110e] border-t border-[#E0A526]/40 px-3 py-2 shadow-[0_-8px_24px_rgba(0,0,0,0.6)]"
             data-testid="bulk-actions-bar">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#E0A526]" data-testid="bulk-selected-count">
                {selected.size} selected
              </span>
              <button onClick={() => setSelected(new Set(assets.map((a) => a.id)))}
                      data-testid="bulk-select-all"
                      className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#9FA8D4] underline underline-offset-2 min-h-[40px]">
                All visible
              </button>
              <button onClick={() => setSelected(new Set())} data-testid="bulk-clear"
                      className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#7B7168] underline underline-offset-2 min-h-[40px]">
                Clear
              </button>
              <button onClick={exitSelect} data-testid="bulk-exit"
                      className="ml-auto w-9 h-9 inline-flex items-center justify-center text-[#A39E93]"><X size={16} /></button>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-1">
              <BulkBtn action="submit" label="Submit" Icon={Send} />
              <BulkBtn action="verify" label="Verify" Icon={BadgeCheck} tone="#3FB950" />
              <BulkBtn action="archive" label="Archive" Icon={Archive} />
              <BulkBtn action="restore" label="Restore" Icon={RotateCcw} />
              <BulkBtn action="publish" label="Publish (Owner)" Icon={CheckCircle2} tone="#3FB950" />
              <BulkBtn action="attach" label="Attach" Icon={Link2}
                       onClick={() => setAttachOpen((v) => !v)} />
            </div>
            {attachOpen && (
              <BulkAttachForm
                onSubmit={(extra) => runBulk("attach", extra)}
                onCancel={() => setAttachOpen(false)} />
            )}
          </div>
        </div>
      )}

      {detail && (
        <DetailSheet token={token} userToken={userToken} asset={detail}
                     onClose={() => setDetail(null)}
                     onChange={(u) => { setAssets((A) => A.map((x) => x.id === u.id ? { ...x, ...u } : x)); setDetail(u); loadHealth(); }}
                     onDeleted={(id) => { setAssets((A) => A.filter((x) => x.id !== id)); setDetail(null); load(); loadHealth(); }}
                     onStepUp={(retry) => setStepupRetry(() => retry)} />
      )}

      {stepupRetry && (
        <StepUpGateModal opToken={token} userToken={userToken}
                         onClose={() => setStepupRetry(null)}
                         onElevated={() => {
                           const retry = stepupRetry;
                           setStepupRetry(null);
                           retry?.();
                         }} />
      )}
    </div>
  );
};

export default AdminMediaLibrary;
